import React from 'react'
import { useRouter } from 'next/navigation'
import { AddCircle } from 'solar-icons'
import { toast } from 'sonner'
import { getLocalLocation, setCookieCartId } from '@/lib/actions'
import { getLocationData } from '@/lib/actions/global/get-location-data'
import { useCheckout, useModals } from '@/lib/hooks'

import { Button, LoadingDots, Separator } from '@/components/ui'

const covers = Array.from({ length: 12 }, (_, i) => i + 1)
const orderTypes = [
  { label: 'In-Store', id: 'in_store' },
  { label: 'Delivery', id: 'delivery' },
  { label: 'Take-Away', id: 'take_away' },
]

export const SelectNewOrderOptions = () => {
  const [tables, setTables] = React.useState<any[]>([])

  const [tableNumber, setTableNumber] = React.useState<number | null>(null)

  const [numberOfCovers, setNumberOfCovers] = React.useState<number | null>(
    null,
  )
  const [selectedOrderType, setSelectedOrderType] =
    React.useState<string>('in_store')

  const [isPending, startTransition] = React.useTransition()

  const { startNewSale } = useCheckout()
  const router = useRouter()

  const [isStartingNewSale, setIsStartingNewSale] = React.useState(false)
  const { closeModal } = useModals()

  const getLocationTableData = React.useCallback(async () => {
    startTransition(async () => {
      const location = await getLocalLocation()
      const locationData = await getLocationData(location?.id as string)
      setTables(locationData)
    })
  }, [])

  React.useEffect(() => {
    getLocationTableData()
  }, [getLocationTableData])

  const handleNewSale = async () => {
    setIsStartingNewSale(true)

    const cart = await startNewSale({
      restaurant_table_id: tableNumber ?? null,
      covers: numberOfCovers ?? null,
      type: selectedOrderType,
      typeLabel: orderTypes.find(
        (orderType) => orderType.id === selectedOrderType,
      )?.label,
    })

    if (!cart) {
      toast.error('Failed to start a new sale')
      setIsStartingNewSale(false)
      return
    }

    await setCookieCartId(cart.id)
    router.push(`/sale?cartId=${cart.id}&newSale=true`)
    closeModal('SELECT_NEW_ORDER_OPTIONS')
  }

  if (isPending) {
    return (
      <div className="flex size-full items-center justify-center">
        <LoadingDots />
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col gap-4 sm:gap-8">
      <div className="flex h-full w-full flex-col gap-4 rounded-lg border border-gray-100 bg-white p-4 shadow-card sm:gap-8 sm:p-8">
        <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-8">
          <div className="flex w-full flex-col gap-4">
            <h2 className="font-medium">Table Number</h2>
            <div className="grid grid-cols-5 gap-2 sm:grid-cols-3">
              {tables?.map((table) => (
                <Button
                  key={table.id}
                  variant={tableNumber === table.id ? 'default' : 'secondary'}
                  size="lg"
                  className="w-full"
                  onClick={() => setTableNumber(table.id)}
                >
                  {table?.number}
                </Button>
              ))}
            </div>
          </div>
          <div className="flex w-full flex-col gap-4">
            <h2 className="font-medium">Number of Covers</h2>
            <div className="grid grid-cols-5 gap-2 sm:grid-cols-3">
              {covers?.map((number: number) => (
                <Button
                  key={number}
                  variant={numberOfCovers === number ? 'default' : 'secondary'}
                  size="lg"
                  className="w-full"
                  onClick={() => setNumberOfCovers(number)}
                >
                  {number}
                </Button>
              ))}
            </div>
          </div>
        </div>
        <Separator />
        <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-8">
          <div className="flex w-full flex-col gap-4">
            <h2 className="font-medium">Order Type</h2>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {orderTypes.map((orderType) => (
                <Button
                  key={orderType.id}
                  variant={
                    selectedOrderType === orderType.id ? 'default' : 'secondary'
                  }
                  size="lg"
                  className="w-full"
                  onClick={() => setSelectedOrderType(orderType.id)}
                >
                  {orderType.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col-reverse items-center gap-2 sm:flex-row sm:gap-4">
        <Button
          variant="secondary"
          size="lg"
          className="w-full"
          onClick={() => closeModal('SELECT_NEW_ORDER_OPTIONS')}
        >
          Cancel
        </Button>
        <Button
          variant="brand"
          size="lg"
          className="w-full"
          disabled={isStartingNewSale}
          onClick={handleNewSale}
        >
          {isStartingNewSale && <LoadingDots dotClassName="bg-white" />}
          {!isStartingNewSale && (
            <>
              <AddCircle className="h-4 w-4" />
              Start New Sale
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
